<template>
  <div
    class="d-user-follow-panel"
    :class="{ 'processing': isProcessing }"
  >
    <div class="user">
      <DUserLinkPopup
        v-if="userUrl"
        :user-id="user.uuid"
        :in-modal="true"
      >
        <template slot="content">
          <a
            class="avatar"
            :href="userUrl"
            target="_blank"
          >
            <img
              :alt="userName"
              class="avatar"
              :src="user.avatar_url"
            >
          </a>
        </template>
      </DUserLinkPopup>

      <span class="info">
        <DUserLinkPopup
          v-if="userUrl"
          :user-id="user.uuid"
          :in-modal="true"
        >
          <template slot="content">
            <a
              class="name"
              :href="userUrl"
              target="_blank"
            >
              <span class="name">{{ userName }}</span>
            </a>
          </template>
        </DUserLinkPopup>
        <span class="tag-line">{{ user.tag_line }}</span>
      </span>
    </div>

    <button
      v-if="user.tag_line !== 'You'"
      class="button-sm button-follow"
      :class="{
        'button-white-bordered-yellow': !isFollowing,
        'button-yellow': isFollowing,
      }"
      @click="followOrUnfollowUser"
    >
      <span v-if="isFollowing">Following</span>
      <span v-else>Follow</span>
    </button>

    <DuiLoadingIndicator
      v-if="isProcessing"
    />
  </div>
</template>

<script>
import Vue from 'vue';

import DUserLinkPopup from '@d-components/d-user-link-popup/d-user-link-popup.vue';
import followersService from '@d-services/followers-service/followers-service';
import URLS from '@d-shared/urls/urls';
import { getErrorMessage } from '@d-shared/utils/errors';

export default {
  components: {
    DUserLinkPopup,
  },
  props: {
    isFollowed: {
      type: Boolean,
      default: false,
    },
    isUserSignedIn: {
      type: Boolean,
      default: false,
    },
    user: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    isFollowing: false,
    isProcessing: false,
    userName: null,
    userUrl: null,
  }),
  mounted() {
    this.$set(this, 'isFollowing', this.isFollowed);
    this.setupUser();
  },
  methods: {
    followOrUnfollowUser() {
      if (!this.isUserSignedIn) {
        Vue.prototype.signupModal.show();
        return;
      }

      if (this.isFollowing) {
        this.stopFollowingUser();
        return;
      }

      this.followUser();
    },

    async followUser() {
      this.$set(this, 'isProcessing', true);

      try {
        await followersService.followUser(this.user.id);
        this.$set(this, 'isFollowing', true);
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }

      this.$set(this, 'isProcessing', false);
    },

    async stopFollowingUser() {
      this.$set(this, 'isProcessing', true);

      try {
        await followersService.stopFollowingUser(this.user.id);
        this.$set(this, 'isFollowing', false);
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }

      this.$set(this, 'isProcessing', false);
    },

    setupUser() {
      const userName = `${ this.user.firstname } ${ this.user.lastname }`;
      this.$set(this, 'userName', userName);
      const userUrl = URLS.app.userProfile.entry.replace('<username>', this.user.username);
      this.$set(this, 'userUrl', userUrl);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@dui-styles/base";

.d-user-follow-panel {
  align-items: center;
  background-color: getColor($white);
  display: flex;
  padding: $_1em;
  position: relative;

  @include transition((
    opacity
  ));

  &.processing {
    opacity: $disabled-opacity;
    pointer-events: none;
  }

  .user {
    align-items: center;
    display: flex;
    flex: 1;
    margin-right: $_1em;

    .avatar img {
      border-radius: 50%;
      height: $_6em;
      margin-right: $_1em;
      width: $_6em;
    }

    .info {
      display: flex;
      flex-direction: column;
      min-width: 0;
      max-width: 4 * $_8em;

      @include screenLessThan(sm) {
        max-width: 2 * $_11em;
      }

      ::v-deep .d-user-link-popup .content {
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .name {
        color: getColor($black);
        font-family: $alt-font;
        font-size: $font-size-lg;
        font-weight: normal;

        @include ellipsis();
      }

      .tag-line {
        color: getColor($grey, dark);
        font-size: $font-size-xs;
      }
    }
  }

  button.button-follow {
    flex: 0 1 auto;
  }

  .dui-loading-indicator {
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate3d(-50%, -50%, 0);
    z-index: 1;
  }
}
</style>
